import React, { useState, useEffect } from 'react';
import {
  Fingerprint, UserCheck, Database, Mail, Phone, MapPin, Eye,
  Share2, UserCog, RefreshCw
} from 'lucide-react';
import api from '../../api/axios';

const PrivacidadPage = () => {
  const [mainBranch, setMainBranch] = useState(null);
  const [loadingBranch, setLoadingBranch] = useState(true);

  useEffect(() => {
    const fetchMainBranch = async () => {
      try {
        setLoadingBranch(true);
        const res = await api.get('/branches');
        const data = res.data?.data || res.data;
        if (Array.isArray(data) && data.length > 0) {
          const principal = data.find(b => b && (b.is_main == 1 || b.is_main === true)) || data[0];
          setMainBranch(principal);
        }
      } catch (error) {
        console.error("Error al obtener sucursal principal para aviso de privacidad:", error);
      } finally {
        setLoadingBranch(false);
      }
    };
    fetchMainBranch();
  }, []);

  const emailContacto = mainBranch?.email_contacto || mainBranch?.email || 'contacto@paqueteriamasenvios.com.mx';
  const telefonoContacto = mainBranch?.phone || mainBranch?.telefono || '';
  const direccionContacto = mainBranch?.direccion || (mainBranch ? `${mainBranch.name}, ${mainBranch.city}` : '');

  const seccionesPrivacidad = [
    {
      id: 1,
      titulo: 'Responsable de tratamiento',
      icono: <UserCheck size={20} className="text-[#CE010B] dark:text-white" />,
      contenido: [
        'La persona física titular de la marca comercial Más Envíos es responsable del tratamiento de sus datos personales.',
      ],
    },
    {
      id: 2,
      titulo: 'Datos personales que recabamos',
      icono: <Database size={20} className="text-[#CE010B] dark:text-white" />,
      contenido: [
        'Para brindar nuestros servicios de paquetería y mensajería recabamos:',
        {
          lista: [
            'Nombre completo del remitente y destinatario',
            'Número telefónico',
            'Correo electrónico',
            'RFC (en caso de facturación)',
            'Datos de pago (cuando aplique)',
          ],
        },
      ],
    },
    {
      id: 3,
      titulo: 'Finalidades del tratamiento',
      icono: <Eye size={20} className="text-[#CE010B] dark:text-white" />,
      contenido: [
        'Sus datos serán utilizados para:',
        {
          lista: [
            'Procesar, trasladar y entregar envíos',
            'Contactar al remitente o destinatario',
            'Emitir facturas fiscales',
            'Atención de dudas y aclaraciones',
          ],
        },
      ],
    },
    {
      id: 4,
      titulo: 'Transferencia de datos',
      icono: <Share2 size={20} className="text-[#CE010B] dark:text-white" />,
      contenido: [
        'Los datos podrán compartirse únicamente con:',
        {
          lista: [
            'Proveedores logísticos o de transporte',
            'Plataformas de pago',
            'Autoridades competentes cuando así lo requiera la ley',
          ],
        },
      ],
    },
    {
      id: 5,
      titulo: 'Derechos Arco',
      icono: <UserCog size={20} className="text-[#CE010B] dark:text-white" />,
      contenido: [
        'Usted puede ejercer sus derechos de Acceso, Rectificación, Cancelación u Oposición enviando su solicitud al correo oficial:',
        `📧 ${emailContacto}`,
      ],
    },
    {
      id: 6,
      titulo: 'Cambios al aviso',
      icono: <RefreshCw size={20} className="text-[#CE010B] dark:text-white" />,
      contenido: [
        'Cualquier modificación será publicada en los medios oficiales de comunicación de Más Envíos.',
      ],
    },
  ];

  return (
    <div className="min-h-screen w-full relative bg-cover bg-center flex flex-col items-center transition-all duration-500"
      style={{ backgroundImage: "url('/fondo-camiones-repartidores.jpg')" }}>

      <header className="z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10 flex flex-col items-center text-center">
        <div className="flex items-center gap-3 md:gap-4 mb-3 group">
          <Fingerprint
            size={42}
            className="text-white group-hover:text-[#CE010B] dark:group-hover:text-white group-hover:scale-110 transition-all duration-300 md:w-[42px] md:h-[42px]"
          />
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight"
            style={{ fontFamily: "'Secular One', sans-serif" }}>
            Aviso de privacidad
          </h1>
        </div>

        {/* Información de Contacto Dinámica de la Sucursal Principal */}
        <div className="mt-4 flex flex-col items-center gap-3 text-white z-10 w-full max-w-xl">
          {emailContacto && (
            <div className="flex items-center justify-center gap-3 bg-black/40 backdrop-blur-sm px-6 py-2.5 rounded-2xl border border-white/20 text-lg md:text-base font-bold w-full max-w-md">
              <Mail size={18} className="shrink-0 text-white" /> <span>{emailContacto}</span>
            </div>
          )}
          {telefonoContacto && (
            <div className="flex items-center justify-center gap-3 bg-black/40 backdrop-blur-sm px-6 py-2.5 rounded-2xl border border-white/20 text-lg md:text-base font-bold w-full max-w-md">
              <Phone size={18} className="shrink-0 text-white" /> <span>{telefonoContacto}</span>
            </div>
          )}
          {direccionContacto && (
            <div className="flex items-center justify-center gap-3 bg-black/40 backdrop-blur-sm px-5 py-3 rounded-2xl border border-white/20 text-lg md:text-xs uppercase font-black tracking-tight text-center leading-tight w-full">
              <MapPin size={18} className="shrink-0 text-white" /> 
              <span>{direccionContacto}</span>
            </div>
          )}
        </div>

        <div className="h-[1px] bg-white/20 w-full max-w-5xl my-6"></div>
        <p className="text-lg md:text-xs text-white uppercase tracking-widest font-black text-center px-4 leading-normal bg-black/20 backdrop-blur-[2px] py-2.5 rounded-xl">
            En cumplimiento con la Ley Federal de Protección de Datos Personales
        </p>
      </header>

      <main className="z-10 w-full max-w-5xl mx-auto px-4 md:px-6 pb-20 mt-2 md:mt-4">
        {/* PANEL GRANDE */}
        <div className="rounded-2xl md:rounded-[3rem] p-6 md:p-12 shadow-2xl flex flex-col gap-6 md:gap-10 transition-all duration-500 border-2 dark:border-[3px]
          bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] border-black/20
          dark:from-[#590000] dark:to-[#780000] dark:border-white"
        >
          {seccionesPrivacidad.map((seccion) => (
            <section key={seccion.id} className="flex flex-col gap-4 md:gap-5 items-center">
              <div className="flex items-center gap-3 border-b pb-2 border-[#CE010B]/50 dark:border-white/30">
                {seccion.icono}
                <h3 className="text-lg md:text-xl font-bold uppercase tracking-tighter text-[#CE010B] dark:text-white"
                  style={{ fontFamily: "'Secular One', sans-serif" }}>
                  {seccion.id}. {seccion.titulo}
                </h3>
              </div>

              {/* CONTENEDORES PEQUEÑOS: Mismo fondo que el grande */}
              <div className="w-full p-5 md:p-6 shadow-xl rounded-2xl transition-all duration-500 border-2 dark:border-[3px]
                bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] border-black/10
                dark:from-[#590000] dark:to-[#780000] dark:border-white"
              >
                {seccion.contenido.map((item, index) => {
                  if (typeof item === 'object' && item.lista) {
                    return (
                      <ul key={index} className="list-disc list-outside text-xl md:text-sm font-medium mt-3 pl-6 pr-2 md:px-10 text-left text-gray-800 dark:text-white">
                        {item.lista.map((li, liIndex) => (
                          <li key={liIndex} className="mb-2 leading-relaxed">{li}</li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={index} className="text-xl md:text-sm font-bold leading-relaxed px-2 md:px-5 text-gray-800 dark:text-white">
                      {item}
                    </p>
                  );
                })}
              </div>
            </section>
          ))}

          <div className="w-full h-[1px] bg-black/10 dark:bg-white/20 my-4"></div>
          <p className="text-sm md:text-xs text-center font-medium text-gray-500 dark:text-white/60">
            Última actualización: Octubre de 2026.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PrivacidadPage;